import { MarkdownRendererConstants } from '../constants';
import { ChatBlkTelemetryService } from '../services';
import {
    ChatBlkPromptSource,
    ChatBlkQuery,
    telemetryFunctionMap,
} from '../types';
import { getFormattedSourceInfo } from '../utils';

const {
    CHATBLK_MFE_FUNCTION_SOURCES_POPOVER_ICON_CLICKED,
    CHATBLK_MFE_FUNCTION_SOURCES_POPOVER_LINK_CLICKED,
    CHATBLK_MFE_FUNCTION_SOURCES_POPOVER_OPENED,
    CHATBLK_MFE_FUNCTION_SOURCES_POPOVER_CLOSED,
} = telemetryFunctionMap;

/**
 * Custom hook that builds and manages the state of a popover for displaying source information.
 *
 * @param href - The URL to be opened when the source icon is clicked.
 * @param sources - An array of source objects containing information to be displayed in the popover.
 * @param query - Optional query object containing additional information for telemetry tracking.
 * @returns An object containing the source number, source details, and telemetry tracking functions.
 *
 * @property {string} sourceNumber - The source number label rebuilt from the link node.
 * @property {ChatBlkPromptSource} source - The source object corresponding to the source number.
 * @property {string} sourceTitle - The formatted title of the source.
 * @property {string} secondaryInfo - Additional formatted information about the source.
 * @property {Function} trackSourceIconClickedTelemetry - Function to track telemetry when the source icon is clicked.
 * @property {Function} trackSourcesLinkTelemetry - Function to track telemetry when the sources link is clicked.
 * @property {Function} trackPopoverOpenedTelemetry - Function to track telemetry when the popover is opened.
 * @property {Function} trackPopoverClosedTelemetry - Function to track telemetry when the popover is closed.
 */
const useSourcePopoverBuilder = (
    linkNode: Element,
    href: string,
    sources: ChatBlkPromptSource[],
    query?: ChatBlkQuery
) => {
    let sourceNumber = '';
    let isPopoverOpen = false;

    /**
     * Recursively rebuilds a source number label by traversing through the children of a given DOM node
     * that resembles a source number markdown links
     *
     * @param node - The DOM element to traverse.
     * @param label - The initial label to be rebuilt.
     * @returns The rebuilt label after traversing all child nodes.
     */
    const rebuildSourceNumber = (node: Element, label: string) => {
        if (!node.children || node.children.length === 0) {
            return label;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Array.from(node.children).forEach((child: any) => {
            if (child.type === 'text') {
                label += child.value;
            }
            if (child.nodeType === Node.ELEMENT_NODE) {
                label = rebuildSourceNumber(child, label);
            }
        });
        return label;
    };

    sourceNumber = rebuildSourceNumber(linkNode, sourceNumber);

    const trackSourceIconClickedTelemetry = () => {
        query &&
            ChatBlkTelemetryService.track(
                CHATBLK_MFE_FUNCTION_SOURCES_POPOVER_ICON_CLICKED,
                { href, label: sourceNumber },
                query.id
            );

        window.open(href, MarkdownRendererConstants.BXF_LINK_TARGET_BLANK);
    };

    const trackSourcesLinkTelemetry = () => {
        ChatBlkTelemetryService.track(
            CHATBLK_MFE_FUNCTION_SOURCES_POPOVER_LINK_CLICKED,
            null,
            query?.id
        );
    };

    const trackPopoverOpenedTelemetry = () => {
        ChatBlkTelemetryService.track(
            CHATBLK_MFE_FUNCTION_SOURCES_POPOVER_OPENED,
            null,
            query?.id
        );
        isPopoverOpen = true;
    };

    const trackPopoverClosedTelemetry = () => {
        /**
         * popoverClosed events are emitted when popovers are unmounted.
         * To avoid duplicate popover closed telemetry events, only track if the panel was
         * previously open and is now being closed
         */
        if (isPopoverOpen) {
            isPopoverOpen = false;
            ChatBlkTelemetryService.track(
                CHATBLK_MFE_FUNCTION_SOURCES_POPOVER_CLOSED,
                null,
                query?.id
            );
        }
    };

    const source: ChatBlkPromptSource = sources[+sourceNumber - 1];
    const { sourceTitle, secondaryInfo } = getFormattedSourceInfo(source) || {};

    return {
        sourceNumber,
        source,
        sourceTitle,
        secondaryInfo,
        trackSourceIconClickedTelemetry,
        trackSourcesLinkTelemetry,
        trackPopoverOpenedTelemetry,
        trackPopoverClosedTelemetry,
    };
};

export default useSourcePopoverBuilder;
